// useExportQrPdf.js
import { useCallback, useState, useEffect } from "react";
import jsPDF from "jspdf";
import QRCode from "qrcode";
import html2canvas from "html2canvas";

function useExportPDF() {
  const exportQrPdf = useCallback(
    async (
      qrValue,
      qrSize,
      fontSize,
      textLabel,
      align = "left",
      exportName = "qrc.pdf",
      heading = null, // optional heading
      logoUrl = null, // optional logo (can be imported image)
      headingSize = 16,
      logoWidth = 30,
      logoHeight = 30,
      startMargin = 20,
      qrcPadding = 0,
      lineHeightFactor = 0.6,
    ) => {
      try {
        const qrDataUrl = await QRCode.toDataURL(qrValue, { width: qrSize });
        const pdf = new jsPDF();
        const pageWidth = pdf.internal.pageSize.getWidth();
        const lineHeight = fontSize * lineHeightFactor;

        let currentY = startMargin; // start margin

        // --- Logo (if available) ---
        if (logoUrl) {
          pdf.addImage(
            logoUrl,
            "PNG",
            pageWidth / 2 - logoWidth / 2,
            currentY,
            logoWidth,
            logoHeight,
          );
          currentY += logoHeight + 10;
        }

        // --- Heading (if available) ---
        if (heading) {
          pdf.setFontSize(headingSize);
          // support multi-line headings
          const lines = heading.split("\n");
          lines.forEach((line) => {
            pdf.text(line, pageWidth / 2, currentY, { align: "center" });
            currentY += lineHeight; // spacing between lines
          });
          currentY += lineHeight; // extra spacing after heading
        }

        // --- Text label ---
        pdf.setFontSize(fontSize);
        let textX, qrX;

        switch (align) {
          case "center":
            textX = pageWidth / 2;
            qrX = pageWidth / 2 - qrSize / 2;
            // pdf.text(textLabel, textX, currentY, { align: "center" });
            if (Array.isArray(textLabel)) {
              textLabel.forEach((line) => {
                pdf.text(line, textX, currentY, { align: "center" });
                currentY += lineHeight; // spacing between lines
              });
            } else {
              pdf.text(textLabel, textX, currentY, { align: "center" });
              currentY += lineHeight;
            }
            break;
          case "right":
            textX = pageWidth - 10;
            qrX = pageWidth - qrSize - 10;
            // pdf.text(textLabel, textX, currentY, { align: "right" });
            if (Array.isArray(textLabel)) {
              textLabel.forEach((line) => {
                pdf.text(line, textX, currentY, { align: "right" });
                currentY += lineHeight; // spacing between lines
              });
            } else {
              pdf.text(textLabel, textX, currentY, { align: "right" });
              currentY += lineHeight;
            }
            break;
          case "left":
          default:
            textX = 10;
            qrX = 10;
            // pdf.text(textLabel, textX, currentY);
            if (Array.isArray(textLabel)) {
              textLabel.forEach((line) => {
                pdf.text(line, textX, currentY);
                currentY += lineHeight; // spacing between lines
              });
            } else {
              pdf.text(textLabel, textX, currentY);
              currentY += lineHeight;
            }
            break;
        }

        // --- QR code ---

        const qrY = currentY + qrcPadding;

        pdf.addImage(qrDataUrl, "PNG", qrX, qrY, qrSize, qrSize);

        pdf.save(exportName);
      } catch (err) {
        console.error("Failed to export QR PDF:", err);
      }
    },
    [],
  );

  const exportQrNodePdf = useCallback(
    async (qrNode, fontSize, textLabel = [], exportName = "qrc.pdf") => {
      try {
        // Capture the AntD QRCode DOM node
        const canvas = await html2canvas(qrNode);
        const imgData = canvas.toDataURL("image/png");
        // Create PDF
        const pdf = new jsPDF();
        pdf.setFontSize(fontSize);
        for (let t of textLabel) {
          pdf.text(t, 10, 20);
        }
        pdf.addImage(imgData, "PNG", 10, 30, 50, 50);

        pdf.save(exportName);
      } catch (err) {
        console.error("Failed to export QR PDF:", err);
      }
    },
    [],
  );

  const generatePdf = useCallback(
    async (contentFn, filename = "document.pdf") => {
      try {
        const pdf = new jsPDF("p", "mm", "a4");
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        // Let the caller draw whatever they want
        await contentFn(pdf, { pageWidth, pageHeight });

        pdf.save(filename);
      } catch (err) {
        console.error("Failed to generate PDF:", err);
      }
    },
    [],
  );

  return {
    exportQrPdf,
    exportQrNodePdf,
    generatePdf /*, qrcSize, qrcValue, fontSize, text, exportName, setQrcSize, setQrcValue, setFontSize, setText, setExportName*/,
  };
}

export default useExportPDF;
